import { Avatar, Col, Divider, Row } from "antd";
import React from "react";


const CDN_TEAM_LOGO = (id) => id ? `https://media.api-sports.io/football/teams/${id}.png` : undefined;
const CDN_LEAGUE_LOGO = (id) => id ? `https://media.api-sports.io/football/leagues/${id}.png` : undefined;

const cx = (...a) => a.filter(Boolean).join(" ");
const pad2 = (n) => String(n).padStart(2, "0");
const fmtDate = (iso) => {
    const d = new Date(iso);
    return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)} | ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

const resultOf = (f) => {
    const sh = f?.score?.fulltime || f?.score?.halftime || {};
    const home = sh.home ?? f?.goals?.home ?? 0;
    const away = sh.away ?? f?.goals?.away ?? 0;
    return { home, away };
};

const winnerSide = (f) => (f?.teams?.home?.winner ? "home" : f?.teams?.away?.winner ? "away" : "draw");

function computeH2HStats(fixtures = [], t1, t2) {
    const stats = {
        total: 0, draws: 0,
        t1Wins: 0, t2Wins: 0,
        t1Goals: 0, t2Goals: 0,
        biggestDraw: null,
        mostEffective: null,
        t1BiggestVictory: null,
        t2BiggestVictory: null,
    };
    let bestDrawGoals = -1;
    let bestMargin = -1;
    let bestT1Margin = -1;
    let bestT2Margin = -1;

    fixtures.forEach((f) => {
        const score = f?.score?.fulltime || f?.score?.halftime || {};
        const home = score.home ?? f?.goals?.home ?? 0;
        const away = score.away ?? f?.goals?.away ?? 0;

        const hid = f?.teams?.home?.id;
        const aid = f?.teams?.away?.id;
        if (!hid || !aid) return;

        const t1Goals = hid === t1 ? home : aid === t1 ? away : 0;
        const t2Goals = hid === t2 ? home : aid === t2 ? away : 0;

        stats.total += 1;
        stats.t1Goals += t1Goals;
        stats.t2Goals += t2Goals;

        if (home === away) {
            stats.draws += 1;
            const g = home + away;
            if (g > bestDrawGoals) {
                bestDrawGoals = g;
                stats.biggestDraw = `${home}-${away}`;
            }
            return;
        }

        if (t1Goals > t2Goals) {
            stats.t1Wins += 1;
            const margin = t1Goals - t2Goals;
            if (margin > bestMargin) stats.mostEffective = `${t1Goals}-${t2Goals}`;
            if (margin > bestT1Margin) {
                bestT1Margin = margin;
                stats.t1BiggestVictory = `${t1Goals}-${t2Goals}`;
            }
        } else {
            stats.t2Wins += 1;
            const margin = t2Goals - t1Goals;
            if (margin > bestMargin) stats.mostEffective = `${t1Goals}-${t2Goals}`;
            if (margin > bestT2Margin) {
                bestT2Margin = margin;
                stats.t2BiggestVictory = `${t1Goals}-${t2Goals}`;
            }
        }
    });

    const avgT1 = stats.total ? stats.t1Goals / stats.total : 0;
    const avgT2 = stats.total ? stats.t2Goals / stats.total : 0;

    return { ...stats, avgT1, avgT2 };
}


function FormBadges({ form = "" }) {
    const lastFive = String(form).trim().slice(0, 5).split("");
    if (!lastFive.length || lastFive[0] === "") return <span className="h2h-form-empty">—</span>;
    return (
        <div className="h2h-form">
            {lastFive.map((c, i) => (
                <span key={i} className={cx("h2h-pill", c === "W" && "h2h-pill--w", c === "D" && "h2h-pill--d", c === "L" && "h2h-pill--l")}>
                    {c}
                </span>
            ))}
        </div>
    );
}


function MatchRow({ f, emphasizeWinner = true }) {
    const compName = f?.league?.name;
    const compLogo = CDN_LEAGUE_LOGO(f?.league?.id);
    const d = fmtDate(f?.fixture?.date);
    const { home, away } = resultOf(f);
    const winner = winnerSide(f);
    const tH = f?.teams?.home?.name;
    const tA = f?.teams?.away?.name;

    return (
        <div className="h2h-item">
            <div className="h2h-col h2h-col--meta">
                <img src={compLogo} alt="" aria-hidden="true" />
                <div>
                    <div className="h2h-comp">{compName}</div>
                    <div className="h2h-date">{d}</div>
                </div>
            </div>
            <div className="h2h-col h2h-col--teams">
                <div className="row">
                    <div className="col-12">
                        <div className={cx("h2h-team", emphasizeWinner && winner === "home" && "is-bold")}>{tH}</div>
                    </div>
                    <div className="col-12">
                        <div className={cx("h2h-team", emphasizeWinner && winner === "away" && "is-bold")}>{tA}</div>
                    </div>
                </div>
            </div>
            <div className="h2h-col h2h-col--teams h2h-col--right" style={{ justifyContent: 'flex-end' }}>
                <div className="row">
                    <div className="col-12">
                        <div className={cx("h2h-team", emphasizeWinner && winner === "home" && "is-bold")}>{home}</div>
                    </div>
                    <div className="col-12">
                        <div className={cx("h2h-team", emphasizeWinner && winner === "away" && "is-bold")}>{away}</div>
                    </div>
                </div>
            </div>
        </div>
    );
}


export default function MatchH2H({
    t1, t2, h2h,       
    t1Name, t2Name,    
    t1Logo, t2Logo,    
    form1, form2,
    last1, last2,
    initialCount = 8, 
}) {
    const [count, setCount] = React.useState(initialCount);

    const crest1 = t1Logo || CDN_TEAM_LOGO(t1);
    const crest2 = t2Logo || CDN_TEAM_LOGO(t2);

    const stats = React.useMemo(() => computeH2HStats(h2h, Number(t1), Number(t2)), [h2h, t1, t2]);

    return (
        <section className="px-container">
            <div className="h2h-wrap">
                <style>{h2hCSS}</style>
                <h5 className="section-title mb-0">
                    Head to Head meetings
                </h5>
                <p className="section-subtitle mb-3">
                    {t1Name} - {t2Name}
                </p>
                <div className="h2h-top-cards">
                    <div className="h2h-card">
                        <div className="h2h-card-label">Matches</div>
                        <div className="h2h-card-value">{stats.total}</div>
                    </div>
                    <div className="h2h-card">
                        <div className="h2h-card-label">The biggest draw</div>
                        <div className="h2h-card-value">{stats.biggestDraw || "—"}</div>
                    </div>
                    <div className="h2h-card">
                        <div className="h2h-card-label">The most effective match</div>
                        <div className="h2h-card-value">{stats.mostEffective || "—"}</div>
                    </div>
                </div>

                <div className="h2h-split">
                    <div className="h2h-duel">
                        <div className="h2h-duel-side">
                            <img className="h2h-crest" src={crest1} alt="" aria-hidden="true" />
                            <div className="h2h-teamname">{t1Name}</div>
                        </div>
                        <div className="h2h-duel-center">
                            <div className="h2h-duel-row">
                                <div className="row">
                                    <div className="col-4" style={{ textAlign: 'center' }}>
                                        <div className="h2h-duel-metric">Wins</div>
                                        <span className="h2h-green">{stats.t1Wins}</span>
                                    </div>
                                    <div className="col-4" style={{ textAlign: 'center' }}>
                                        <div className="h2h-duel-metric">Draws</div>
                                        <span className="h2h-gray">{stats.draws}</span>
                                    </div>
                                    <div className="col-4" style={{ textAlign: 'center' }}>
                                        <div className="h2h-duel-metric">Loses</div>
                                        <span className="h2h-red">{stats.t2Wins}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="h2h-duel-side h2h-right">
                            <img className="h2h-crest" src={crest2} alt="" aria-hidden="true" />
                            <div className="h2h-teamname">{t2Name}</div>
                        </div>
                    </div>

                    <div className="h2h-sidebox">
                        <div className="h2h-sidegrid">
                            <Row gutter={24} align='middle'>
                                <Col xl={8} lg={8} md={8} sm={8} xs={8}>
                                    <p className="h2h-slabel">Victories</p>
                                </Col>
                                <Col xl={4} lg={4} md={4} sm={4} xs={4}>
                                    <p className="h2h-sv">{stats.t1Wins}</p>
                                </Col>
                                <Col xl={4} lg={4} md={4} sm={4} xs={4}>
                                    <Avatar size={40} style={{ padding: 5 }} src={crest1} />
                                </Col>
                                <Col xl={4} lg={4} md={4} sm={4} xs={4}>
                                    <Avatar size={40} style={{ padding: 5 }} src={crest2} />
                                </Col>
                                <Col xl={4} lg={4} md={4} sm={4} xs={4}>
                                    <p className="h2h-sv">{stats.t2Wins}</p>
                                </Col>
                            </Row>
                            <Divider className="w-100 mt-2 mb-2" />
                            <Row gutter={24} align='middle'>
                                <Col xl={8} lg={8} md={8} sm={8} xs={8}>
                                    <p className="h2h-slabel">Scored goals</p>
                                </Col>
                                <Col xl={4} lg={4} md={4} sm={4} xs={4}>
                                    <p className="h2h-sv">{stats.t1Goals}</p>
                                </Col>
                                <Col xl={4} lg={4} md={4} sm={4} xs={4}>
                                    <Avatar size={40} style={{ padding: 5 }} src={crest1} />
                                </Col>
                                <Col xl={4} lg={4} md={4} sm={4} xs={4}>
                                    <Avatar size={40} style={{ padding: 5 }} src={crest2} />
                                </Col>
                                <Col xl={4} lg={4} md={4} sm={4} xs={4}>
                                    <p className="h2h-sv">{stats.t2Goals}</p>
                                </Col>
                            </Row>
                            <Divider className="w-100 mt-2 mb-2" />
                            <Row gutter={24} align='middle'>
                                <Col xl={8} lg={8} md={8} sm={8} xs={8}>
                                    <p className="h2h-slabel">Average per game</p>
                                </Col>
                                <Col xl={4} lg={4} md={4} sm={4} xs={4}>
                                    <p className="h2h-sv">{stats.avgT1.toFixed(2)}</p>
                                </Col>
                                <Col xl={4} lg={4} md={4} sm={4} xs={4}>
                                    <Avatar size={40} style={{ padding: 5 }} src={crest1} />
                                </Col>
                                <Col xl={4} lg={4} md={4} sm={4} xs={4}>
                                    <Avatar size={40} style={{ padding: 5 }} src={crest2} />
                                </Col>
                                <Col xl={4} lg={4} md={4} sm={4} xs={4}>
                                    <p className="h2h-sv">{stats.avgT2.toFixed(2)}</p>
                                </Col>
                            </Row>
                            <Divider className="w-100 mt-2 mb-2" />
                            <Row gutter={24} align='middle'>
                                <Col xl={8} lg={8} md={8} sm={8} xs={8}>
                                    <p className="h2h-slabel">The bigest victory</p>
                                </Col>
                                <Col xl={4} lg={4} md={4} sm={4} xs={4}>
                                    <p className="h2h-sv">{stats.t1BiggestVictory || "—"}</p>
                                </Col>
                                <Col xl={4} lg={4} md={4} sm={4} xs={4}>
                                    <Avatar size={40} style={{ padding: 5 }} src={crest1} />
                                </Col>
                                <Col xl={4} lg={4} md={4} sm={4} xs={4}>
                                    <Avatar size={40} style={{ padding: 5 }} src={crest2} />
                                </Col>
                                <Col xl={4} lg={4} md={4} sm={4} xs={4}>
                                    <p className="h2h-sv">{stats.t2BiggestVictory || "—"}</p>
                                </Col>
                            </Row>
                        </div>
                    </div>
                </div>

                <div className="h2h-list">
                    {h2h.slice(0, count).map((f) => (
                        <MatchRow key={f?.fixture?.id} f={f} />
                    ))}

                    {h2h.length > count && (
                        <button className="h2h-more" onClick={() => setCount((c) => c + initialCount)}>
                            Show more matches
                        </button>
                    )}
                </div>

                <div className="h2h-last">
                    <LastBlock title={`Last matches:`} crest={crest1} fixtures={last1} form={form1} />
                    <LastBlock title={`Last matches:`} crest={crest2} fixtures={last2} form={form2} />
                </div>
            </div>
        </section>
    );
}

function LastBlock({ title, crest, fixtures = [], form = "" }) {
    let wins = 0, draws = 0, losses = 0, goals = 0;
    fixtures.forEach(f => {
        const { home, away } = resultOf(f);
        const isHome = f?.teams?.home?.name && title.includes(f?.teams?.home?.name);
        goals += home + away;

        const w = winnerSide(f);
        if (w === "draw") draws++; else if ((w === "home" && isHome) || (w === "away" && !isHome)) wins++; else losses++;
    });
    const matches = fixtures.length;
    const gpg = matches ? (goals / matches).toFixed(1) : "0.0";

    return (
        <div className="h2h-last-card">
            <div className="h2h-last-head">
                <h4><img src={crest} alt="" aria-hidden="true" /> {title}</h4>
                <FormBadges form={form} />
            </div>

            <div className="h2h-last-metrics">
                <div><div className="h2h-mv">{matches}</div><div className="h2h-ml">Matches</div></div>
                <div><div className="h2h-mv">{goals}</div><div className="h2h-ml">Goals</div></div>
                <div><div className="h2h-mv">{gpg}</div><div className="h2h-ml">Goal/game</div></div>
                <div><div className="h2h-mv h2h-green">{wins}</div><div className="h2h-ml">Wins</div></div>
                <div><div className="h2h-mv">{draws}</div><div className="h2h-ml">Draws</div></div>
                <div><div className="h2h-mv h2h-red">{losses}</div><div className="h2h-ml">Losses</div></div>
            </div>

            <div className="h2h-last-list">
                {fixtures.map(f => <MatchRow key={f?.fixture?.id} f={f} />)}
            </div>

            {fixtures.length >= 5 && (
                <button className="h2h-more">Show more matches</button>
            )}
        </div>
    );
}

const h2hCSS = `
.px-container { background:#f7f7f9; padding:30px; }
@media (max-width: 520px) { .px-container { padding:16px; } }
.h2h-wrap { max-width: 1380px; margin: 0 auto; }
.h2h-title { margin:0 0 12px; font-size:20px; font-weight:800; color:#0f172a; }
.h2h-strong { font-weight:900; }

/* top cards */
.h2h-top-cards { display:grid; grid-template-columns: repeat(3, 1fr); gap:12px; margin-bottom:14px; }
.h2h-card { background:#fff; border:1px solid rgba(2,6,23,.08); border-radius:10px; padding:12px; }
.h2h-card-label { color:#64748b; font-weight:500; font-size:16px; }
.h2h-card-value { color:#0f172a; font-weight:500; font-size:34px; margin-top:6px; }

@media (max-width: 600px) {
  .h2h-top-cards {
    grid-template-columns: 1fr;   /* single column */
  }

  .h2h-teamname {
    display: none;
  }
}

/* duel + side stats */
.h2h-split { display:grid; grid-template-columns: 1.3fr 1fr; gap:16px; margin-bottom:16px; }
.h2h-duel { background:#fff; border:1px solid rgba(2,6,23,.08); border-radius:10px; padding:16px; display:grid; grid-template-columns: 1fr auto 1fr; align-items:center; }
.h2h-duel-side { display:flex; flex-direction:column; align-items:center; gap:6px; }
.h2h-crest { width:56px; height:56px; object-fit:contain; }
.h2h-teamname { font-weight:800; color:#0f172a; text-align:center; }
.h2h-duel-center { padding:0 10px; }
.h2h-duel-row { display:flex; align-items:center; justify-content:space-between; gap:12px; }
.h2h-duel-metric { color:#64748b; font-weight:500; }
.h2h-duel-values { font-size:34px; font-weight:500; }
.h2h-green { color:#16a34a; font-size: 40px; font-weight: 600; } .h2h-red { color:#ef4444; font-size: 40px; font-weight: 600; } .h2h-gray { color:#64748b; font-size: 40px; font-weight: 600; }
.h2h-right { align-items:center; }

.h2h-sidebox { background:#fff; border:1px solid rgba(2,6,23,.08); border-radius:10px; padding:16px; }
.h2h-sidegrid { align-items:center; }
.h2h-slabel { color:#64748b; font-weight:500; }
.h2h-sv { font-weight:600; font-size: 20px; color:#0f172a; }

/* list */
/* map columns to grid areas */
.h2h-col--meta  { grid-area: meta; }
.h2h-col--teams { grid-area: teams; }
.h2h-col--right { grid-area: score; }

/* desktop layout (unchanged, just explicit areas) */
.h2h-item {
  display: grid;
  align-items: center;
  grid-template-columns: 1.3fr 1fr 1fr;
  grid-template-areas: "meta teams score";
  gap:8px; padding:12px 14px; border-top:1px solid rgba(2,6,23,.06);
}

/* mobile: meta on top, teams left + score right in SAME row */
@media (max-width: 600px) {
  .h2h-item {
  gap:8px; padding:12px 14px; border-top:1px solid rgba(2,6,23,.06);
    grid-template-columns: 1fr 90px;           /* left: teams, right: score */
    grid-template-areas:
      "meta  meta"                              /* meta full width on its own row */
      "teams score"; 
  }

  .h2h-col--meta {
    align-items: center;
    padding-bottom: 6px;
    border-bottom: 1px solid rgba(2,6,23,.06);
  }

  .h2h-col--teams { justify-content: flex-start; }
  .h2h-col--right { justify-content: flex-end; } /* keep score to the right */
}

.h2h-list { background:#fff; border:1px solid rgba(2,6,23,.08); border-radius:10px; overflow:hidden; margin-bottom:16px; }
.h2h-item:first-child { border-top:none; }
.h2h-item:hover { background:#f8fafc; }
.h2h-col--meta { display:flex; gap:10px; align-items:center; }
.h2h-col--meta img { width:24px; height:24px; object-fit:contain; }
.h2h-comp { font-weight:800; color:#0f172a; }
.h2h-date { color:#64748b; font-size:13px; }
.h2h-col--teams { display:flex; gap:8px; align-items:center; justify-content:flex-start; }
.h2h-col--right { justify-content:flex-end; }
.h2h-team { color:#0f172a; font-weight:600; }
.h2h-team.is-bold { font-weight:900; }
.h2h-score { margin-left:auto; font-weight:900; color:#0f172a; }
.h2h-more { display:block; width:100%; text-align:center; padding:10px; background:transparent; color:#2563eb; border:none; font-weight:700; cursor:pointer; }
.h2h-more:hover { text-decoration:underline; }

.h2h-skel { padding:12px; } .h2h-skel-row { height:40px; border-radius:8px; background:linear-gradient(90deg,#f1f5f9 25%,#e2e8f0 37%,#f1f5f9 63%); background-size:400% 100%; animation:h2h-shimmer 1.2s infinite linear; margin:6px 0; }
@keyframes h2h-shimmer { 0% {background-position: 100% 0} 100% {background-position: -100% 0} }
.h2h-err { padding:12px 14px; color:#991b1b; background:#fee2e2; border-top:1px solid #fecaca; }

/* last blocks */
.h2h-last { display:grid; grid-template-columns: 1fr 1fr; gap:16px; }
.h2h-last-card { background:#fff; border:1px solid rgba(2,6,23,.08); border-radius:10px; }
.h2h-last-head { display:flex; align-items:center; justify-content:space-between; padding:12px 14px; border-bottom:1px solid rgba(2,6,23,.06); }
.h2h-last-head h4 { margin:0; font-size:16px; font-weight:800; color:#0f172a; display:flex; align-items:center; gap:8px; }
.h2h-last-head img { width:20px; height:20px; object-fit:contain; }
.h2h-form { display:flex; gap:6px; }
.h2h-pill { display:inline-flex; align-items:center; justify-content:center; height:22px; min-width:22px; padding:0 6px; border-radius:6px; font-size:12px; font-weight:800; color:#fff; }
.h2h-pill--w { background:#22c55e; } .h2h-pill--d { background:#94a3b8; } .h2h-pill--l { background:#ef4444; }
.h2h-form-empty { color:#94a3b8; }

.h2h-last-metrics { display:grid; grid-template-columns: repeat(6, 1fr); padding:12px 14px; gap:10px; }
.h2h-mv { font-weight:600; font-size: 24px; color:#0f172a; text-align:center; }
.h2h-ml { color:#64748b; font-size:14px; text-align:center; }
.h2h-last-list { border-top:1px solid rgba(2,6,23,.06); }

@media (max-width: 900px) {
  .h2h-split { grid-template-columns: 1fr; }
  .h2h-last { grid-template-columns: 1fr; }
}
@media (max-width: 600px) {
  .h2h-item { grid-template-columns: 1fr; }
  .h2h-col--right { justify-content:flex-start; }
}
`;
